import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import axiosInstance from 'utils/axiosUtil';
import { TitleTooltip } from 'components';
import {
  getStarkProviderTotals,
  listStarkLogs,
} from 'store/actions/starkActions';

import StarkDialog from './StarkDialog';
import { STARK_CATEGORY_MAP, getCmsAnnualLimit } from './starkConstants';

// ── Helpers ────────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  ok:       'success',
  warning:  'warning',
  exceeded: 'error',
};

const STATUS_LABEL = {
  ok:       'Okay',
  warning:  'Near Limit',
  exceeded: 'Over Limit',
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear, currentYear - 1, currentYear - 2].filter((y) => y >= 2024);

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

function formatAmount(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return '$0.00';
  return `$${n.toFixed(2)}`;
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = (theme) => ({
  sectionCard: {
    marginBottom: theme.spacing(3),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  spacer: { flex: 1 },
  tableHead: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    '& th': {
      fontSize: 12,
      fontWeight: 'inherit',
      padding: 2,
      verticalAlign: 'top',
    },
  },
  tableBody: {
    fontWeight: 300,
    '& td': {
      fontSize: 12,
      padding: '2px 4px',
      fontWeight: 'inherit',
    },
  },
  providerRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.03)',
    },
  },
  expandedRow: {
    backgroundColor: 'rgba(0,0,0,0.02)',
    padding: theme.spacing(1, 2),
  },
  progressBar: {
    height: 6,
    borderRadius: 3,
    width: 80,
    display: 'inline-block',
    verticalAlign: 'middle',
    marginRight: theme.spacing(1),
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  entrySubTable: {
    '& td, & th': {
      fontSize: 11,
      padding: '2px 8px',
    },
  },
  emptyMsg: {
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    padding: theme.spacing(2),
    textAlign: 'center',
  },
});

// ── Sub-component: expanded provider detail ─────────────────────────────────────

class ProviderDetail extends Component {
  render() {
    const { entries, providerId, onEditEntry, classes } = this.props;
    const providerEntries = (entries || []).filter((log) =>
      (log.allocations || []).some((a) => a.provider_id === providerId)
    );

    if (providerEntries.length === 0) {
      return <Typography className={classes.emptyMsg}>No entries for this provider.</Typography>;
    }

    return (
      <Table size="small" className={classes.entrySubTable}>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right">Allocated</TableCell>
            <TableCell>Logged By</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {providerEntries.map((log) => {
            const alloc = (log.allocations || []).find((a) => a.provider_id === providerId);
            return (
              <TableRow key={log.id}>
                <TableCell>{formatDate(log.spending_date)}</TableCell>
                <TableCell>{STARK_CATEGORY_MAP[log.category] || log.category}</TableCell>
                <TableCell>{log.description || '—'}</TableCell>
                <TableCell align="right">{formatAmount(alloc?.allocated_amount)}</TableCell>
                <TableCell>{log.user_name || '—'}</TableCell>
                <TableCell>
                  {!log.locked && (
                    <Button size="small" onClick={() => onEditEntry(log)}>
                      Edit
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }
}

// ── Main component ─────────────────────────────────────────────────────────────

class StarkTrackingSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      year:           currentYear,
      myEntriesOnly:  false,
      expandedRows:   {},  // { [providerId]: bool }
      dialogOpen:     false,
      editLog:        null,
    };
  }

  componentDidMount() {
    this._fetchTotals();
    this._fetchLogs();
  }

  componentDidUpdate(prevProps, prevState) {
    const { year, myEntriesOnly } = this.state;
    const { clientId, currentUserId } = this.props;

    if (
      year !== prevState.year ||
      myEntriesOnly !== prevState.myEntriesOnly ||
      clientId !== prevProps.clientId
    ) {
      this._fetchTotals();
      this._fetchLogs();
    }
  }

  _fetchTotals() {
    const { dispatch, clientId, currentUserId } = this.props;
    const { year, myEntriesOnly } = this.state;
    if (!clientId) return;
    const filterUserId = myEntriesOnly ? currentUserId : undefined;
    dispatch(getStarkProviderTotals(clientId, year, filterUserId));
  }

  _fetchLogs() {
    const { dispatch, clientId, currentUserId } = this.props;
    const { year, myEntriesOnly } = this.state;
    if (!clientId) return;
    const filterUserId = myEntriesOnly ? currentUserId : undefined;
    dispatch(listStarkLogs(clientId, { year, filterUserId }));
  }

  toggleRow = (providerId) => {
    this.setState((prev) => ({
      expandedRows: {
        ...prev.expandedRows,
        [providerId]: !prev.expandedRows[providerId],
      },
    }));
  };

  openEditDialog = (log) => {
    this.setState({ dialogOpen: true, editLog: log });
  };

  onDialogClose = () => {
    this.setState({ dialogOpen: false, editLog: null });
  };

  onEntrySaved = () => {
    this.setState({ dialogOpen: false, editLog: null });
    this._fetchTotals();
    this._fetchLogs();
  };

  onEntryDeleted = () => {
    this.setState({ dialogOpen: false, editLog: null });
    this._fetchTotals();
    this._fetchLogs();
  };

  exportCsv = () => {
    const { clientId, currentUserId } = this.props;
    const { year, myEntriesOnly }     = this.state;
    const filterUserId = myEntriesOnly ? currentUserId : undefined;

    const params = new URLSearchParams({ clientId, year });
    if (filterUserId) params.set('filterUserId', filterUserId);

    axiosInstance
      .get(`/v2/stark/export?${params.toString()}`, { responseType: 'blob' })
      .then((res) => {
        const url  = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href  = url;
        link.setAttribute('download', `StarkLaw_${year}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => console.error('Stark CSV export failed', err));
  };

  render() {
    const { classes, clientId, currentUserId, providerTotals, starkLogs } = this.props;
    const { year, myEntriesOnly, expandedRows, dialogOpen, editLog } = this.state;

    const totals  = providerTotals?.data || [];
    const loading = providerTotals?.loading || false;
    const logs    = starkLogs?.data || [];

    const annualLimit = totals.length > 0
      ? totals[0].annual_limit
      : getCmsAnnualLimit(year);

    return (
      <>
        <Card className={classes.sectionCard}>
          <CardContent>
            {/* Header row */}
            <div className={classes.cardHeader}>
              <TitleTooltip
                title='Stark Law Tracking'
                onExport={this.exportCsv}
              />

              <div className={classes.spacer} />

              {/* Annual limit badge */}
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1, alignSelf: 'center' }}>
                {year} limit: <strong>{formatAmount(annualLimit)}</strong>
              </Typography>

              {/* My entries only toggle */}
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={myEntriesOnly}
                    onChange={(e) => this.setState({ myEntriesOnly: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">My entries only</Typography>}
                sx={{ mr: 1 }}
              />

              {/* Year selector */}
              <FormControl size="small" sx={{ minWidth: 90 }}>
                <InputLabel id="stark-year-label">Year</InputLabel>
                <Select
                  labelId="stark-year-label"
                  value={year}
                  label="Year"
                  onChange={(e) => this.setState({ year: Number(e.target.value) })}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Loading */}
            {loading && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 2 }} />}

            {/* Empty state */}
            {!loading && totals.length === 0 && (
              <Typography className={classes.emptyMsg}>
                No non-monetary compensation logged for {year}.
              </Typography>
            )}

            {/* Provider totals table */}
            {!loading && totals.length > 0 && (
              <Table size="small">
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell style={{ width: 24 }} />
                    <TableCell>Provider</TableCell>
                    <TableCell align="right">YTD Total</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody className={classes.tableBody}>
                  {totals.map((row) => {
                    const expanded   = Boolean(expandedRows[row.provider_id]);
                    const pct        = Math.min(row.pct_used || 0, 100);
                    const color      = STATUS_COLOR[row.status] || 'primary';

                    return (
                      <React.Fragment key={row.provider_id}>
                        {/* Summary row */}
                        <TableRow
                          className={classes.providerRow}
                          onClick={() => this.toggleRow(row.provider_id)}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            {row.last_name}, {row.first_name}
                            {row.npi && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                NPI {row.npi}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {formatAmount(row.ytd_total)}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                color={color}
                                className={classes.progressBar}
                              />
                              <Typography variant="caption">
                                {pct}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={STATUS_LABEL[row.status] || row.status}
                              color={color}
                              variant='outlined'
                              size='small'
                              sx={{
                                width: 88,
                                borderWidth: 1,
                                '& .MuiChip-label': { color: 'text.primary', fontWeight: 300, textAlign: 'center', width: '100%' },
                              }}
                            />
                          </TableCell>
                        </TableRow>

                        {/* Expanded entry detail */}
                        <TableRow>
                          <TableCell colSpan={5} sx={{ padding: 0, borderBottom: expanded ? undefined : 'none' }}>
                            <Collapse in={expanded} unmountOnExit>
                              <Box className={classes.expandedRow}>
                                <ProviderDetail
                                  entries={logs}
                                  providerId={row.provider_id}
                                  onEditEntry={this.openEditDialog}
                                  classes={classes}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit dialog — opened from expanded provider row */}
        {dialogOpen && (
          <StarkDialog
            open={dialogOpen}
            onClose={this.onDialogClose}
            onSaved={this.onEntrySaved}
            onDeleted={this.onEntryDeleted}
            clientId={clientId}
            log={editLog}
            providers={[]}
          />
        )}
      </>
    );
  }
}

StarkTrackingSection.propTypes = {
  clientId:       PropTypes.number.isRequired,
  currentUserId:  PropTypes.number.isRequired,
  dispatch:       PropTypes.func.isRequired,
  providerTotals: PropTypes.object,
  starkLogs:      PropTypes.object,
  classes:        PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    providerTotals: state.stark?.providerTotals || { data: [], loading: false, error: null },
    starkLogs:      state.stark?.logs           || { data: [], loading: false, error: null },
  };
}

export default connect(mapStateToProps)(withStyles(styles)(StarkTrackingSection));
